import { useState } from 'react';
import { messages } from '../../messages';
import { ApiClient } from '../../api/client';
import WebApp from '@twa-dev/sdk';

interface UploadAreaProps {
  creationId: string;
  onUploadComplete: () => void;
  pricingInfo?: string;
}

export function UploadArea({ creationId, onUploadComplete, pricingInfo }: UploadAreaProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || isUploading) return;

    setIsUploading(true);
    try {
      const response = await ApiClient.uploadFile(file, (progress) => {
        setUploadProgress(progress);
        const uploadButton = document.querySelector('.upload-button') as HTMLElement;
        if (uploadButton) {
          uploadButton.style.setProperty('--upload-progress', `${progress}%`);
        }
      });

      await ApiClient.attachFiles(creationId, [response.id]);
      onUploadComplete();
    } catch (error) {
      console.error('Failed to upload file:', error);
      const tg = WebApp;
      await tg.showPopup({
        title: 'Error',
        message: messages.errors.fileUpload,
        buttons: [{ type: 'ok' }]
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      const uploadButton = document.querySelector('.upload-button') as HTMLElement;
      if (uploadButton) {
        uploadButton.style.setProperty('--upload-progress', '0%');
      }
    }
  };

  return (
    <div className="bg-surface-interactive dark:bg-surface-interactive-dark border border-interactive dark:border-interactive-dark rounded-2xl p-6 shadow-interactive">
      <div className="flex justify-between items-center mb-5">
        <h3 className="text-lg font-semibold text-theme-text">{messages.creationDetails.uploadVideo.title}</h3>
        {pricingInfo && (
          <div className="text-xs text-theme-hint bg-neutral-badge dark:bg-neutral-badge-dark px-2 py-1 rounded-full">
            {pricingInfo}
          </div>
        )}
      </div>
      <label className={`relative flex flex-col items-center justify-center h-60 border-2 border-dashed border-primary-accent rounded-xl bg-surface-interactive-alpha dark:bg-surface-interactive-dark-alpha cursor-pointer transition-all ${isUploading ? 'opacity-90 cursor-not-allowed' : 'hover:bg-surface-interactive-hover dark:hover:bg-surface-interactive-dark-hover'}`}>
        <input
          type="file"
          accept="video/*"
          onChange={handleFileSelect}
          disabled={isUploading}
          className="hidden"
        />
        <span className="material-symbols-outlined text-3xl text-primary-accent mb-4">upload</span>
        {isUploading ? (
          <div className="flex flex-col items-center gap-2 w-4/5 max-w-xs">
            <div className="text-base font-medium text-primary-accent">
              {messages.creationDetails.uploadVideo.uploadProgress.replace('{progress}', uploadProgress.toString())}
            </div>
            <div className="w-full h-1 bg-neutral-fill dark:bg-neutral-fill-dark rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary-accent transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        ) : (
          <span className="text-base font-medium text-primary-accent">{messages.creationDetails.uploadVideo.dropzoneText}</span>
        )}
      </label>
    </div>
  );
} 